import type { ExtraFieldsValues } from '@shared/model'
import type { TaskPriority, TasksKanbanTask } from '@entities/task'
import type { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

export interface KanbanTaskUpdateSubmitPayload {
  status: TasksKanbanStage
  priority?: TaskPriority | null
  deadline: string | null
  assignee_id: number | null
  waiting_reason?: string | null
  cancel_reason?: string | null
  resolution?: string | null
  customer_outcome?: string | null
  extra_fields: ExtraFieldsValues
}

interface KanbanTaskUpdateSubmitApiOptions {
  id: number | string
  task: KanbanTaskUpdateSubmitPayload
  onStart?: () => void
  onSuccess?: (task: TasksKanbanTask) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useKanbanTaskUpdateSubmitApi = async (
  options: KanbanTaskUpdateSubmitApiOptions,
): Promise<TasksKanbanTask | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.kanban-tasks.update', { id: options.id })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.patch(url!, {
      task: options.task,
    })
    const task: TasksKanbanTask = data.data

    commonStore.setLoading(false)
    options.onSuccess?.(task)

    return task
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}